/**
 * G13: one digest (morning / afternoon / evening), decrypted here. Counts are exact; every item
 * opens its evidence; a project heading opens that project's changelog.
 */
import { Link } from '@tanstack/react-router'
import {
  CHANGELOG_KIND_LABEL,
  CHANGELOG_KINDS,
  DIGEST_SLOT_LABEL,
  type DigestReport,
} from '@viko/protocol'
import { Chip } from '@viko/transcript-ui'
import { projectHref } from '../lib/ia.js' // ia-shell
import { ChangelogList } from './Changelog.js'
import { Ago } from './ui.js'

const fmt = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })

export function DigestView({
  digest,
  perSection = 5,
}: {
  digest: DigestReport
  perSection?: number
}) {
  return (
    <div className="grid gap-3" data-testid="digest-view">
      <p className="m-0 text-[14px] text-text-2">
        {DIGEST_SLOT_LABEL[digest.slot]} digest · {fmt(digest.from)} → {fmt(digest.to)}
      </p>
      {digest.sections.length ? null : (
        <p className="m-0 text-[14px] text-text-3">Nothing verified changed in this window.</p>
      )}
      {digest.sections.map((s) => (
        <section key={s.projectId} aria-label={s.projectName ?? s.projectId}>
          <h3 className="m-0 mb-1 text-[14px] font-medium">
            <Link to={projectHref(s.projectId, 'history')}>{s.projectName ?? s.projectId}</Link>
          </h3>
          <div className="mb-1 flex flex-wrap gap-1" role="group" aria-label="Counts">
            {CHANGELOG_KINDS.flatMap((k) =>
              s.counts[k]
                ? [<Chip key={k}>{`${CHANGELOG_KIND_LABEL[k]} ${s.counts[k]}`}</Chip>]
                : [],
            )}
          </div>
          <ChangelogList items={s.items} limit={perSection} />
          {s.more ? (
            <p className="m-0 text-[14px] text-text-3">
              …and {s.more} more in the{' '}
              <Link to={projectHref(s.projectId, 'history')}>changelog</Link>
            </p>
          ) : null}
        </section>
      ))}
      {digest.email ? (
        <p className="m-0 text-[14px] text-text-3" data-testid="digest-email-state">
          Email{' '}
          {digest.email.state === 'sent'
            ? `sent to ${digest.email.recipients ?? 0} recipient${digest.email.recipients === 1 ? '' : 's'}`
            : digest.email.state === 'failed'
              ? `failed: ${digest.email.detail ?? 'unknown error'}`
              : `not sent: ${digest.email.detail ?? 'skipped'}`}{' '}
          <Ago ts={digest.email.at} />
        </p>
      ) : null}
    </div>
  )
}
